import React from 'react';
import type { TFunction } from 'i18next';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';

import type { Option } from 'src/types';
import { useMarkedOptionLabel } from './useMarkedOptionLabel';
import type { MarkedOptionLabelProps } from './useMarkedOptionLabel';

/**
 * Provides `formatOptionLabel` and `getOptionValue` for select fields that accept
 * DtoType directly, removing the need to wrap values in `Option<IdDto>`.
 *
 * `formatOption` converts a DtoType into an `Option` internally so that marked/active
 * styling still works via `useMarkedOptionLabel`.
 *
 * @example Migration from `Option<IdDto>` to DtoType usage without `Option` wrapper
 *
 * --- Schema (before) ---
 * advertiserId: optionSchema(z.custom<IdDto>())
 *
 * --- Schema (after) ---
 * advertiserId: z.custom<DtoType>()
 *
 * --- loadOptions (before) ---
 * const loadOptions = async (filter: string) => {
 *   const { data } = await getItems.load({ filter });
 *   return data.items.map((item) => ({ label: item.name, value: item.id })); <- we don't have DtoType here for reuse it if it's needed
 * };
 *
 * --- loadOptions (after) ---
 * const loadOptions = async (filter: string) => {
 *   const { data } = await getItems.load({ filter });
 *   return data.items; <- return the DtoType items directly
 * };
 *
 * --- Usual usage ---
 * const useDtoSpecificOptions = useDtoOptions<DtoType>({ t, formatOption: (value) => ({ label: value.name, value: value.id }) });
 * <AsyncSelectField{...otherProps} {...useDtoSpecificOptions}  />
 *
 * --- Usage options ---
 * const useDtoSpecificOptions = useDtoOptions<Dto>({ <- pass the DTO type
 *   t, <- pass the translation function
 *   marker, <- pass the marker props to apply the marked/active styling
 *   formatOption: (value) => ({ <- pass the function to convert the DTO to the Option
 *     label: formatOptionLabel(value.name), <- pass the function to format the label
 *     value: value.id, <- pass the value of the DTO
 *   }),
 *   getOptionValue: (value) => value.id.toString(), <- pass the function to get the value of the DTO if it's not the id field
 * });
 * <AsyncSelectField {...advertiserOptions} loadOptions={loadOptions} />
 */
interface DtoOptionsProps<T> {
  t: TFunction;
  marker?: MarkedOptionLabelProps;
  formatOption: (selectedItem: T) => Option<unknown>;
  getOptionValue?: (value: T) => string;
}

type IdBase =
  | string
  | {
      toString: () => string;
    };

interface ItemBase<TId extends IdBase> {
  id?: TId;
}

export const useDtoOptions = <T extends ItemBase<IdBase> | string>({
  t,
  marker,
  formatOption,
  getOptionValue,
}: DtoOptionsProps<T>): {
  formatOptionLabel: (selectedItem: T) => React.ReactNode | string;
  getOptionValue: (value: T) => string;
} => {
  const markedOptionLabel = useMarkedOptionLabel({ t, ...marker });

  const handleGetOptionValue = useCallbackRef((value: T) => {
    if (typeof value === 'string') {
      return value;
    }
    return getOptionValue?.(value) ?? value.id?.toString() ?? value.toString();
  });

  // Need to use useCallback because the formatOption can be a non stable function
  // eslint-disable-next-line no-restricted-syntax
  const formatOptionLabel = React.useCallback(
    (selectedItem: T) => {
      return markedOptionLabel(formatOption(selectedItem));
    },
    [markedOptionLabel, formatOption],
  );

  return {
    formatOptionLabel,
    getOptionValue: handleGetOptionValue,
  };
};
